import { z } from 'zod';

// ---------------------------------------------------------------- planes y modelos

export const BOT_PLANS = ['BASICO', 'ESTANDAR', 'PREMIUM'] as const;
export type BotPlan = (typeof BOT_PLANS)[number];
export const BOT_PLAN_LABELS: Record<BotPlan, string> = {
  BASICO: 'Básico (sin inteligencia artificial)',
  ESTANDAR: 'Estándar',
  PREMIUM: 'Premium',
};

/** Tareas que el bot delega a un modelo de lenguaje. */
export type LlmTask = 'understand' | 'reply';

export interface LlmUsage {
  input: number;
  cachedInput: number;
  output: number;
}

// ---------------------------------------------------------------- personalidad

export const BOT_TREATMENTS = ['USTED', 'TU'] as const;
export type BotTreatment = (typeof BOT_TREATMENTS)[number];
export const BOT_STYLES = ['CERCANA', 'FORMAL', 'ENTUSIASTA'] as const;
export type BotStyle = (typeof BOT_STYLES)[number];
export const BOT_STYLE_LABELS: Record<BotStyle, string> = {
  CERCANA: 'Cercana y breve',
  FORMAL: 'Formal',
  ENTUSIASTA: 'Entusiasta',
};

const shortText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));

/** Una personalidad por empresa (ADR-020, decisión 2): campos separados, nunca un texto libre gigante. */
export const botPersonaSchema = z.object({
  /** Cómo se presenta ante el cliente (VYVIA en V&V). */
  name: z.string().trim().min(1).max(40).default('Asistente'),
  treatment: z.enum(BOT_TREATMENTS).default('USTED'),
  style: z.enum(BOT_STYLES).default('CERCANA'),
  /** Saludo del primer mensaje; vacío = el bot arma uno con su nombre y el proyecto. */
  greeting: shortText(300),
  farewell: shortText(200),
  /** Frases o expresiones que sí usa / que nunca usa. */
  doSay: z.array(z.string().trim().min(1).max(120)).max(10).default([]),
  dontSay: z.array(z.string().trim().min(1).max(120)).max(10).default([]),
  /** Indicaciones por tema (actúan por dentro; de cara al cliente es una sola persona). */
  topics: z
    .object({
      qualification: shortText(600),
      pricing: shortText(600),
      appointments: shortText(600),
      objections: shortText(600),
    })
    .default({}),
});
export type BotPersona = z.infer<typeof botPersonaSchema>;
export const DEFAULT_BOT_PERSONA: BotPersona = botPersonaSchema.parse({});

// ---------------------------------------------------------------- lo que sabe el bot por proyecto

export const BOT_PRICE_MODES = ['DESDE', 'NO'] as const;
export type BotPriceMode = (typeof BOT_PRICE_MODES)[number];

export const projectBotSchema = z.object({
  /** Indicaciones propias del proyecto (estacionamientos, reglas, qué destacar). */
  instructions: shortText(2000),
  faqs: z
    .array(
      z.object({
        q: z.string().trim().min(3).max(200),
        a: z.string().trim().min(1).max(600),
      }),
    )
    .max(20)
    .default([]),
  /** null = hereda el modo de la empresa. */
  priceMode: z.enum(BOT_PRICE_MODES).nullable().default(null),
  /** Precios "desde" escritos a mano (reemplazan a los calculados desde el stock). */
  pricesFrom: z
    .array(
      z.object({
        typology: z.string().trim().min(1).max(60),
        from: z.number().positive(),
        currency: z.enum(['USD', 'PEN']).default('USD'),
      }),
    )
    .max(20)
    .default([]),
});
export type ProjectBot = z.infer<typeof projectBotSchema>;
export type ProjectBotPayload = z.input<typeof projectBotSchema>;
export const DEFAULT_PROJECT_BOT: ProjectBot = projectBotSchema.parse({});

// ---------------------------------------------------------------- entender, decidir, responder

export const BOT_INTENTS = [
  'SALUDO',
  'INTERES',
  'PREGUNTA_PRECIO',
  'PREGUNTA_FINANCIAMIENTO',
  'PREGUNTA_UBICACION',
  'PREGUNTA_ENTREGA',
  'PREGUNTA_OTRA',
  'PIDE_CITA',
  'ACEPTA_CITA',
  'PROPONE_HORARIO',
  'RECHAZA_HORARIO',
  'NO_INTERESA',
  'PIDE_HUMANO',
  'AGRADECE',
  'FUERA_DE_TEMA',
  'STOP',
] as const;
export type BotIntent = (typeof BOT_INTENTS)[number];
export const BOT_INTENT_LABELS: Record<BotIntent, string> = {
  SALUDO: 'Saluda',
  INTERES: 'Muestra interés',
  PREGUNTA_PRECIO: 'Pregunta por precio',
  PREGUNTA_FINANCIAMIENTO: 'Pregunta por financiamiento',
  PREGUNTA_UBICACION: 'Pregunta por ubicación',
  PREGUNTA_ENTREGA: 'Pregunta por entrega o avance de obra',
  PREGUNTA_OTRA: 'Otra pregunta',
  PIDE_CITA: 'Pide una cita',
  ACEPTA_CITA: 'Acepta el horario',
  PROPONE_HORARIO: 'Propone otro horario',
  RECHAZA_HORARIO: 'Rechaza el horario',
  NO_INTERESA: 'No le interesa',
  PIDE_HUMANO: 'Pide hablar con una persona',
  AGRADECE: 'Agradece',
  FUERA_DE_TEMA: 'Fuera de tema',
  STOP: 'Pide que no le escriban',
};

export const BOT_TEMPERATURES = ['FRIO', 'TIBIO', 'CALIENTE'] as const;
export type BotTemperature = (typeof BOT_TEMPERATURES)[number];

/** Datos que el bot extrae de la conversación (mismos conceptos que el perfil de compra). */
export const botExtractedSchema = z.object({
  name: z.string().trim().min(1).max(60).nullable().optional(),
  budget: z.number().positive().nullable().optional(),
  budgetCurrency: z.enum(['USD', 'PEN']).nullable().optional(),
  bedrooms: z.number().int().min(0).max(10).nullable().optional(),
  district: z.string().trim().min(2).max(80).nullable().optional(),
  horizon: z
    .enum(['INMEDIATO', 'MENOS_3_MESES', 'DE_3_A_6_MESES', 'DE_6_A_12_MESES', 'MAS_12_MESES'])
    .nullable()
    .optional(),
  purpose: z.enum(['VIVIENDA', 'INVERSION']).nullable().optional(),
  financing: z.enum(['CONTADO', 'HIPOTECARIO', 'MIXTO', 'CREDITO_DIRECTO']).nullable().optional(),
  /** Horario que el cliente acepta o propone, tal como lo dijo. */
  appointmentText: z.string().trim().max(120).nullable().optional(),
  appointmentMode: z.enum(['PRESENCIAL', 'VIRTUAL']).nullable().optional(),
});
export type BotExtracted = z.infer<typeof botExtractedSchema>;

export const botUnderstandingSchema = z.object({
  intent: z.enum(BOT_INTENTS),
  temperature: z.enum(BOT_TEMPERATURES),
  data: botExtractedSchema.default({}),
  /** Una línea para el asesor: qué dijo el cliente. */
  summary: z.string().trim().max(300).default(''),
  /** 0–1: cuánta confianza tiene el modelo (las reglas devuelven 1 cuando hay palabra clave exacta). */
  confidence: z.number().min(0).max(1).default(0.7),
});
export type BotUnderstanding = z.infer<typeof botUnderstandingSchema>;

/** Esquema JSON que se le pide al modelo en la tarea "understand" (mismo contrato que el Zod de arriba). */
export const BOT_UNDERSTANDING_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  required: ['intent', 'temperature', 'data', 'summary', 'confidence'],
  properties: {
    intent: { type: 'string', enum: [...BOT_INTENTS] },
    temperature: { type: 'string', enum: [...BOT_TEMPERATURES] },
    data: {
      type: 'object',
      additionalProperties: false,
      properties: {
        name: { type: ['string', 'null'] },
        budget: { type: ['number', 'null'] },
        budgetCurrency: { type: ['string', 'null'], enum: ['USD', 'PEN', null] },
        bedrooms: { type: ['integer', 'null'] },
        district: { type: ['string', 'null'] },
        horizon: {
          type: ['string', 'null'],
          enum: [
            'INMEDIATO',
            'MENOS_3_MESES',
            'DE_3_A_6_MESES',
            'DE_6_A_12_MESES',
            'MAS_12_MESES',
            null,
          ],
        },
        purpose: { type: ['string', 'null'], enum: ['VIVIENDA', 'INVERSION', null] },
        financing: {
          type: ['string', 'null'],
          enum: ['CONTADO', 'HIPOTECARIO', 'MIXTO', 'CREDITO_DIRECTO', null],
        },
        appointmentText: { type: ['string', 'null'] },
        appointmentMode: { type: ['string', 'null'], enum: ['PRESENCIAL', 'VIRTUAL', null] },
      },
    },
    summary: { type: 'string' },
    confidence: { type: 'number' },
  },
} as const;

export const BOT_ACTIONS = [
  'RESPONDER',
  'PROPONER_CITA',
  'AGENDAR_CITA',
  'ESCALAR',
  'CERRAR',
  'DETENER',
  'ESPERAR',
] as const;
export type BotAction = (typeof BOT_ACTIONS)[number];
export const BOT_ACTION_LABELS: Record<BotAction, string> = {
  RESPONDER: 'Responder y seguir calificando',
  PROPONER_CITA: 'Proponer una cita',
  AGENDAR_CITA: 'Agendar la cita aceptada',
  ESCALAR: 'Escalar al asesor (prioridad)',
  CERRAR: 'Proponer el descarte',
  DETENER: 'Detener: el cliente pidió que no le escriban',
  ESPERAR: 'No responder por ahora',
};

/** Preguntas de calificación en el orden en que el bot las hace (una por turno). */
export const QUALIFICATION_STEPS = ['purpose', 'bedrooms', 'budget', 'horizon'] as const;
export type QualificationStep = (typeof QUALIFICATION_STEPS)[number];

export interface BotSlot {
  /** ISO en UTC. */
  at: string;
  /** "mañana jueves 26 a las 11:00 a. m." */
  label: string;
}

export interface BotDecision {
  action: BotAction;
  /** Por qué (para la traza y la auditoría). */
  reason: string;
  /** Marca PRIORIDAD y avisa al asesor (independiente de la acción). */
  priority: boolean;
  /** Siguiente pregunta de calificación cuando la acción es RESPONDER. */
  nextQuestion: QualificationStep | null;
  /** Horarios a ofrecer cuando la acción es PROPONER_CITA. */
  slots: BotSlot[];
  /** Cita a registrar cuando la acción es AGENDAR_CITA. */
  appointment: { at: string; mode: 'PRESENCIAL' | 'VIRTUAL'; label: string } | null;
}

export const BOT_STAGES = [
  'INICIO',
  'CALIFICANDO',
  'PROPONIENDO_CITA',
  'CITA_AGENDADA',
  'ESCALADO',
  'CERRADO',
  'DETENIDO',
] as const;
export type BotStage = (typeof BOT_STAGES)[number];

// ---------------------------------------------------------------- plan Básico guiado (8.5c)

export type BotMenuKind = 'PRINCIPAL' | 'purpose' | 'bedrooms' | 'budget' | 'horizon' | 'slots';

export interface BotMenuOption {
  /** Número que escribe el cliente. */
  n: number;
  label: string;
  /** Intención que representa la opción (menú principal y horarios). */
  intent?: BotIntent;
  /** Datos que aporta la opción (preguntas de calificación). */
  data?: Partial<BotExtracted>;
  /** Índice del horario ofrecido (menú de horarios). */
  slotIndex?: number;
}

/** Opciones numeradas que el bot ofreció en su último mensaje (solo plan Básico): así "2" significa algo. */
export interface BotMenu {
  kind: BotMenuKind;
  options: BotMenuOption[];
}

/** Estado de una conversación del bot (vive en la sesión del simulador; en la 8.5b, en la conversación del lead). */
export interface BotConversationState {
  stage: BotStage;
  /** Turnos del cliente procesados. */
  turns: number;
  /** Mensajes que el bot envió. */
  botMessages: number;
  /** Preguntas de calificación ya hechas. */
  asked: QualificationStep[];
  /** Datos acumulados de toda la conversación. */
  data: BotExtracted;
  /** Últimos horarios ofrecidos (para entender "sí, el segundo"). */
  proposedSlots: BotSlot[];
  /** Cuántas veces se propuso cita sin cerrarla. */
  proposals: number;
  /** Última temperatura vista. */
  temperature: BotTemperature;
  /** Opciones numeradas vigentes (plan Básico guiado, 8.5c); ausente o null en los demás planes. */
  menu?: BotMenu | null;
}

export const INITIAL_BOT_STATE: BotConversationState = {
  stage: 'INICIO',
  turns: 0,
  botMessages: 0,
  asked: [],
  data: {},
  proposedSlots: [],
  proposals: 0,
  temperature: 'FRIO',
  menu: null,
};

export interface BotMessage {
  role: 'customer' | 'bot';
  text: string;
  at: string;
}

/** Límites que el motor aplica (vienen de la configuración de la empresa). */
export interface BotLimits {
  maxTurns: number;
  maxAttempts: number;
}

// ---------------------------------------------------------------- ficha de conocimiento

export interface BotKnowledgeTypology {
  typology: string;
  available: number;
  bedrooms: number | null;
  areaMin: number | null;
  areaMax: number | null;
  /** Precio "desde" (menor precio disponible o el escrito a mano); null = no se informa. */
  priceFrom: number | null;
  currency: 'USD' | 'PEN';
}

export interface BotKnowledge {
  company: {
    name: string;
    persona: BotPersona;
    businessHours: { start: string; end: string; days: number[] };
    timezone: string;
    priceMode: BotPriceMode;
    disclose: boolean;
  };
  project: {
    id: string;
    name: string;
    district: string | null;
    address: string | null;
    constructionStatus: string | null;
    deliveryDate: string | null;
    /** Horario de la sala de ventas (el del proyecto o, si no tiene, el de la empresa). */
    salesRoomHours: { start: string; end: string; days: number[] };
    /** Estacionamientos y depósitos disponibles (no cuentan como tipología de vivienda). */
    extras: Array<{
      label: string;
      available: number;
      priceFrom: number | null;
      currency: 'USD' | 'PEN';
    }>;
    commercial: {
      reservationAmount: number | null;
      reservationCurrency: 'USD' | 'PEN';
      initialFeePercent: number | null;
      initialInstallments: number | null;
      financingTypes: string[];
      banks: string[];
      programs: string[];
      promotions: string | null;
      notes: string | null;
    };
    subdivisions: string[];
    typologies: BotKnowledgeTypology[];
    availableUnits: number;
    faqs: Array<{ q: string; a: string }>;
    instructions: string | null;
    priceMode: BotPriceMode;
  };
  lead: {
    firstName: string | null;
    medium: string | null;
    campaign: string | null;
    known: Partial<
      Record<'budget' | 'bedrooms' | 'district' | 'purpose' | 'financing' | 'horizon', string>
    >;
    tags: string[];
    custom: Array<{ label: string; value: string }>;
    recentHumanActivity: string[];
  } | null;
}
